// Живая проверка по СТО прямо в тексте: волнистое подчёркивание, подсказка с пунктом СТО и кнопкой «Исправить».
// Правила - по абзацам (типографика, «рис.», номера и ссылки [N], набранные вручную, смешанные алфавиты) и по
// узлам (названия объектов, заголовки, перечисления, примечания, сноски, битые ссылки). Проверка идёт через
// 300 мс после правки; до этого подчёркивания сдвигаются вместе с текстом.
import { Extension, type Editor } from '@tiptap/core';
import { Plugin, PluginKey, TextSelection, type EditorState } from '@tiptap/pm/state';
import { Decoration, DecorationSet, type EditorView } from '@tiptap/pm/view';
import type { Node as PMNode } from '@tiptap/pm/model';
import { numberingStore } from '../../core/editor/numberingPlugin';
import type { Numbering } from '../../core/numbering';
import { DEFS_ALIASES, STRUCT_KINDS } from '../../core/schema/constants';
import { appStore } from '../../core/store';
import { blockText, type BlockText } from './textmap';
import { typoEdits, type TypoEdit } from './typography';
import { overlay } from './overlay';
import { ignores, prefs } from './profile';
import { capFirst, NB } from './data';
import { NBSP_CHAR } from '../../core/schema/constants';
import { isProperWord, properWordsOf } from '../../core/editor/listCase';

export interface Fix { label: string; apply: (ed: Editor) => void }
export interface InlineIssue {
  key: string; from: number; to: number; sev: 'E' | 'W' | 'I'; code: string; msg: string; rule?: string;
  node: string | null; text: string; fixes: Fix[];
}

// ------------------------------------------------------------------------------------------------ помощники
const replaceFix = (label: string, from: number, to: number, insert: string): Fix => ({
  label, apply: (ed) => { const tr = ed.state.tr; if (insert) tr.insertText(insert, from, to); else tr.delete(from, to); ed.view.dispatch(tr); },
});
const LATIN_LOOK = 'AaBCcEeHKkMOoPpTXxy';
const CYR_LOOK = 'АаВСсЕеНКкМОоРрТХху';
function toCyr(s: string) { return s.replace(/[AaBCcEeHKkMOoPpTXxy]/g, (ch) => CYR_LOOK[LATIN_LOOK.indexOf(ch)]); }
function toLat(s: string) { return s.replace(/[АаВСсЕеНКкМОоРрТХху]/g, (ch) => LATIN_LOOK[CYR_LOOK.indexOf(ch)]); }

/** Объект по номеру: 'fig', '3' -> id. */
function targetByNum(nb: Numbering, kind: string, num: string): string | null {
  for (const id of nb.order) { const i = nb.byId.get(id); if (i && i.kind === kind && i.num === num) return id; }
  return null;
}
function sourceByNum(nb: Numbering, n: number): string | null {
  for (const [id, k] of nb.sources) if (k === n) return id;
  return null;
}
const KIND_OF_WORD = (w: string) => (/^рис/i.test(w) ? 'fig' : /^табл/i.test(w) ? 'tbl' : /^листинг/i.test(w) ? 'lst' : /^приложени/i.test(w) ? 'app' : /^формул/i.test(w) ? 'eq' : '');
const WORD_FORM: Record<string, string> = { fig: 'рисунке', tbl: 'таблице' };
const CASES: Record<string, Record<string, string>> = {
  fig: { nom: 'рисунок', gen: 'рисунка', dat: 'рисунку', prep: 'рисунке' },
  tbl: { nom: 'таблица', gen: 'таблицы', dat: 'таблице', prep: 'таблице' },
};
/** Падеж слова по предлогу перед ним: «на рисунке 1», «из таблицы 2», «к рисунку 3», без предлога - «рисунок 1». */
export function caseByPrep(kind: 'fig' | 'tbl' | string, before: string): string {
  const f = CASES[kind] || CASES.fig;
  const prep = (before.toLowerCase().match(/(?:^|[\s(\u00a0])(на|в|во|о|об|при|по|к|ко|согласно|из|до|для|от|у|без|после|вместо|кроме)[\s\u00a0]*$/) || [])[1];
  if (!prep) return f.nom;
  if (/^(по|к|ко|согласно)$/.test(prep)) return f.dat;
  if (/^(из|до|для|от|у|без|после|вместо|кроме)$/.test(prep)) return f.gen;
  return f.prep;
}

const typoCache = new Map<string, TypoEdit[]>();
function cachedTypo(text: string) {
  let r = typoCache.get(text);
  if (!r) { r = typoEdits(text).filter((e) => !e.quiet); if (typoCache.size > 3000) typoCache.clear(); typoCache.set(text, r); }
  return r;
}

// ------------------------------------------------------------------------------------------------ правила
interface Env { nb: Numbering; out: InlineIssue[]; listPunctAuto: boolean; doc?: PMNode; proper?: Set<string> }

function add(env: Env, node: string | null, from: number, to: number, sev: InlineIssue['sev'], code: string, msg: string,
  rule: string | undefined, fixes: Fix[], text = '') {
  env.out.push({ key: `${code}|${node || ''}|${text}`, from, to, sev, code, msg, rule, node, text, fixes });
}

function textRules(env: Env, bt: BlockText, blockId: string | null, parentType: string) {
  const t = bt.text;
  const P = (i: number) => bt.map[i];
  // типографика (кроме «тихих» - их ставит writer)
  for (const e of cachedTypo(t)) {
    const q = e.code === 'TYPO-QUOTES';
    add(env, blockId, P(e.from), P(e.to), q ? 'W' : 'I', e.code, e.msg + ' - при экспорте исправится автоматически', '4.1',
      [replaceFix(`Заменить на «${e.insert.split(NBSP_CHAR).join('␣')}»`, P(e.from), P(e.to), e.insert),
        { label: 'Исправить типографику во всём абзаце', apply: (ed) => { void import('./typography').then((m) => m.applyTypography(ed, new Set([blockId || '']))); } }],
      t.slice(e.from, e.to));
  }
  // «рис. 3», «табл. 2» -> живая ссылка
  for (const m of t.matchAll(/(?<![А-Яа-яЁё])(рис|табл)\.[  ]?(\d+(?:\.\d+)?|[А-ЯЁ]\.\d+)/gi)) {
    const kind = /^рис/i.test(m[1]) ? 'fig' : 'tbl';
    const tid = targetByNum(env.nb, kind, m[2]);
    const a = P(m.index!); const b = P(m.index! + m[0].length);
    const fixes: Fix[] = [];
    const word = caseByPrep(kind, t.slice(Math.max(0, m.index! - 30), m.index!));
    if (tid) fixes.push({ label: `«${word} ${m[2]}» - живая ссылка`, apply: (ed) => ed.chain().insertContentAt({ from: a, to: b },
      [{ type: 'text', text: word + NB }, { type: 'xref', attrs: { target: tid } }]).run() });
    fixes.push(replaceFix(`Написать «${kind === 'fig' ? 'рисунок' : 'таблица'}» полностью`, a, P(m.index! + m[1].length + 1), kind === 'fig' ? 'рисунок' : 'таблица'));
    add(env, blockId, a, b, 'E', kind === 'fig' ? 'FIG-REF-ABBR' : 'TBL-REF-ABBR',
      `«${m[1]}.» в ссылке не сокращают - пишите «${kind === 'fig' ? 'рисунок' : 'таблица'} ${m[2]}»`, kind === 'fig' ? '4.5.4' : '4.9.3', fixes, m[0]);
  }
  // номер объекта, набранный вручную: «на рисунке 3», «в таблице 2», «(приложение А)»
  if (parentType !== 'caption' && parentType !== 'appendixTitle') {
    for (const m of t.matchAll(/(?<![А-Яа-яЁё])(рисун[а-яё]*|таблиц[а-яё]*|листинг[а-яё]*|приложени[а-яё]*|формул[а-яё]*)[  ]+(\(?(\d+(?:\.\d+)?|[А-ЯЁ]\.\d+)\)?|[А-ЯЁ](?![А-Яа-яЁё]))/gi)) {
      const kind = KIND_OF_WORD(m[1]);
      const num = m[3] || m[2];
      if (kind === 'eq' && !m[2].startsWith('(')) continue;       // «формула 2» - скорее текст
      if (kind === 'app' && !/^[А-ЯЁ]$/.test(num)) continue;
      if (kind !== 'app' && /^[А-ЯЁ]$/.test(num)) continue;
      const tid = targetByNum(env.nb, kind, num);
      const a = P(m.index! + m[0].length - m[2].length); const b = P(m.index! + m[0].length);
      add(env, blockId, a, b, 'W', 'REF-MANUAL', `Номер «${m[2]}» набран вручную - при добавлении объектов он не изменится. Вставьте живую ссылку (@)`,
        '4.5.4, 4.9.3', tid ? [{ label: 'Заменить живой ссылкой', apply: (ed) => ed.chain().insertContentAt({ from: a, to: b }, { type: 'xref', attrs: { target: tid } }).run() }] : [],
        m[0]);
    }
    // ссылка на источник, набранная вручную: «[3]», «[3, с. 5]»
    for (const m of t.matchAll(/\[(\d+)(?:,[  ]*(с\.[  ]*[\d–-]+))?\]/g)) {
      const sid = sourceByNum(env.nb, Number(m[1]));
      const a = P(m.index!); const b = P(m.index! + m[0].length);
      add(env, blockId, a, b, 'W', 'CITE-MANUAL', 'Ссылка [N] набрана вручную - номер не обновится при добавлении источников',
        '4.14', sid ? [{ label: 'Заменить живой ссылкой на источник', apply: (ed) => ed.chain().insertContentAt({ from: a, to: b },
          { type: 'cite', attrs: { items: [{ source: sid, loc: (m[2] || '').replace(/ /g, NB) }] } }).run() }] : [], m[0]);
    }
  }
  // незавершённая «@»-ссылка (список закрыли, «@» остался в тексте)
  for (const m of t.matchAll(/(?<=^|[\s( «])@[A-Za-zА-Яа-яЁё0-9]*/g)) {
    const a = P(m.index!); const b = P(m.index! + m[0].length);
    if (overlay.getState().mention?.from === a) continue;          // список «@» ещё открыт - пользователь выбирает
    add(env, blockId, a, b, 'E', 'MENTION-LEFT', `Незавершённая ссылка «${m[0]}» - выберите объект из списка или удалите`, '4.14',
      [replaceFix('Удалить', a, b, '')], m[0]);
  }
  // знак сноски - перед знаком препинания («слово¹.»), а не после него
  bt.atoms.forEach((node, i) => {
    if (node.type.name !== 'footnote' || i === 0 || !/[.,:;!?…]/.test(t[i - 1])) return;
    const fp = P(i);
    add(env, blockId, P(i - 1), fp + 1, 'E', 'FOOTNOTE-MARK-POS', 'Знак сноски ставят перед знаком препинания: «слово¹.»', '4.10.9',
      [{ label: 'Поставить перед знаком', apply: (ed) => {
        const fn = ed.state.doc.nodeAt(fp);
        if (fn?.type.name !== 'footnote') return;
        ed.view.dispatch(ed.state.tr.delete(fp, fp + 1).insert(fp - 1, fn));
      } }], node.attrs.id);
  });
  // смешанные алфавиты в слове («Cистема» с латинской C)
  for (const m of t.matchAll(/[A-Za-zА-Яа-яЁё]+/g)) {
    const w = m[0];
    if (!/[A-Za-z]/.test(w) || !/[А-Яа-яЁё]/.test(w)) continue;
    const cyr = (w.match(/[А-Яа-яЁё]/g) || []).length; const lat = w.length - cyr;
    const fixed = cyr >= lat ? toCyr(w) : toLat(w);
    const a = P(m.index!); const b = P(m.index! + w.length);
    add(env, blockId, a, b, 'E', 'TYPO-MIXED-ALPHABET', `В слове «${w}» смешаны русские и латинские буквы - проверка орфографии и поиск его не увидят`,
      '4.1', /[A-Za-zА-Яа-яЁё]/.test(fixed) && fixed !== w && !(/[A-Za-z]/.test(fixed) && /[А-Яа-яЁё]/.test(fixed)) ? [replaceFix(`Заменить на «${fixed}»`, a, b, fixed)] : [], w);
  }
}

function nodeRules(env: Env, node: PMNode, pos: number, parent: PMNode, index: number) {
  const t = node.type.name;
  const id = (node.attrs?.id as string) || null;
  if (t === 'caption') {
    const bt = blockText(node, pos);
    const s = bt.text;
    const owner = parent.attrs?.id || null;
    const what = { figure: 'рисунка', stoTable: 'таблицы', listing: 'листинга' }[parent.type.name] || '';
    const rule = { figure: '4.5.7', stoTable: '4.9.4', listing: '6.1' }[parent.type.name];
    // «Рисунок 3 – », «Таблица А.1.», «Рис. 2», «Листинг –» - набранные вручную слово и номер; «Таблица истинности»,
    // «Рисунок художника» - это название (без номера и тире после слова)
    const mm = s.match(/^(?:Рисунок|Рис\.|Таблица|Табл\.|Листинг)(?:[ \u00a0]*(?:[А-ЯЁ]\.)?\d+(?:\.\d+)*\.?(?:[ \u00a0]*[–—:-])?|[ \u00a0]*[–—:-])[ \u00a0]*/);
    if (mm && mm[0].trim()) add(env, owner, bt.map[0], bt.map[mm[0].length], 'E', 'CAPTION-MANUAL',
      `Слово и номер («${mm[0].trim()}») ставятся автоматически - наберите только название ${what}`, rule,
      [replaceFix('Убрать', bt.map[0], bt.map[mm[0].length], '')], mm[0]);
    const end = s.match(/[  ]*\.$/);
    if (end && !/\.\.$/.test(s) && !/(?:^|[\s ])\S\.$/.test(s)) add(env, owner, bt.map[s.length - end[0].length], bt.map[s.length], 'E', 'CAPTION-DOT',
      `Точка в конце названия ${what} не ставится`, rule, [replaceFix('Убрать точку', bt.map[s.length - end[0].length], bt.map[s.length], '')], 'точка');
    const first = s.replace(/^[\s ]+/, '');
    if (!mm && /^[а-яё]/.test(first)) {
      const i = s.length - first.length;
      add(env, owner, bt.map[i], bt.map[i + 1], 'W', 'CAPTION-CASE', `Название ${what} пишут с прописной буквы`, rule,
        [replaceFix('Сделать прописной', bt.map[i], bt.map[i + 1], first[0].toUpperCase())], first.slice(0, 20));
    }
    return;
  }
  if (t === 'heading') {
    const bt = blockText(node, pos);
    const s = bt.text;
    if (!s.trim()) return;
    const end = s.match(/[  ]*\.$/);
    if (end && !/\.\.$/.test(s) && !/(?:^|[\s ])\S\.$/.test(s)) add(env, id, bt.map[s.length - end[0].length], bt.map[s.length], 'E', 'HEAD-DOT',
      'Точка в конце заголовка не ставится', '4.2.5', [replaceFix('Убрать точку', bt.map[s.length - end[0].length], bt.map[s.length], '')], 'точка');
    const num = s.match(/^[  ]*(\d+(?:\.\d+)*)\.?[  ]+(?=\S)/);
    if (num) add(env, id, bt.map[0], bt.map[num[0].length], 'W', 'HEAD-NUM-MANUAL', `Номер «${num[1]}» ставится автоматически - наберите только название`,
      '4.4', [replaceFix('Убрать номер', bt.map[0], bt.map[num[0].length], '')], num[1]);
    const hk = s.trim().toLowerCase().replace(/ё/g, 'е').replace(/\.$/, '');
    const struct = DEFS_ALIASES.includes(hk) ? ['defs', STRUCT_KINDS.defs] as [string, string]
      : Object.entries(STRUCT_KINDS).find(([k, v]) => k !== 'terms' && k !== 'abbrev' && v.toLowerCase() === hk);
    if (struct) add(env, id, bt.map[0], bt.map[s.length], 'E', 'HEAD-STRUCT',
      `«${struct[1]}» - структурный элемент: он не нумеруется и оформляется по центру (4.2.2)`, '4.2.2',
      [{ label: `Сделать структурным элементом «${struct[1]}»`, apply: (ed) => {
        const tr = ed.state.tr.replaceWith(pos, pos + node.nodeSize, ed.schema.nodes.structHeading.create({ kind: struct[0] }));
        ed.view.dispatch(tr);
      } }], s);
    const letters = s.replace(/[^А-Яа-яЁёA-Za-z]/g, '');
    if (letters.length > 4 && letters === letters.toUpperCase()) {
      const fixed = capFirst(s.toLowerCase());
      add(env, id, bt.map[0], bt.map[s.length], 'W', 'HEAD-CAPS', 'Заголовок прописными буквами - пишут с прописной, далее строчными', '4.2.5',
        [replaceFix('Как в предложении', bt.map[0], bt.map[s.length], fixed)], s.slice(0, 30));
    } else if (/^[а-яё]/.test(s.trim()) && !num) {
      const i = s.search(/\S/);
      add(env, id, bt.map[i], bt.map[i + 1], 'W', 'HEAD-CASE', 'Заголовок пишут с прописной буквы', '4.2.5',
        [replaceFix('Сделать прописной', bt.map[i], bt.map[i + 1], s[i].toUpperCase())], s.slice(0, 30));
    }
    let underline = false;
    node.descendants((n) => { if (n.marks?.some((m) => m.type.name === 'underline')) underline = true; return true; });
    if (underline) add(env, id, bt.map[0], bt.map[s.length], 'E', 'HEAD-UNDERLINE', 'Подчёркивание в заголовках не допускается', '4.2.5',
      [{ label: 'Снять подчёркивание', apply: (ed) => ed.view.dispatch(ed.state.tr.removeMark(pos, pos + node.nodeSize, ed.schema.marks.underline)) }], s.slice(0, 30));
    return;
  }
  if (t === 'stoList' && parent.type.name !== 'stoListItem') {
    // обобщающая фраза с двоеточием перед перечислением (4.4)
    const prev = index > 0 ? parent.child(index - 1) : null;
    let prevPos = pos;
    if (prev) prevPos = pos - prev.nodeSize;
    if (prev && prev.type.name === 'paragraph' && prev.content.size) {
      const bt = blockText(prev, prevPos);
      const s = bt.text.replace(/[\s ]+$/, '');
      if (!/:$/.test(s)) {
        const tail = s.match(/[.;,]$/);
        const a = bt.map[s.length - (tail ? 1 : 0)]; const b = bt.map[s.length];
        add(env, prev.attrs.id || null, Math.max(bt.map[0], bt.map[s.length] - 1), bt.map[s.length], 'E', 'LIST-INTRO',
          'Перед перечислением - обобщающая фраза с двоеточием в конце', '4.4.5', [replaceFix('Поставить «:»', a, b, ':')], s.slice(-30));
      }
    } else if (!prev || prev.type.name !== 'paragraph') {
      add(env, id, pos + 1, Math.min(pos + 4, pos + node.nodeSize), 'E', 'LIST-INTRO', 'Перед перечислением нужна обобщающая фраза с двоеточием', '4.4.5', [], 'нет фразы');
    }
    // 4.4.4: «1)» - только для детализации вложенного перечисления; уровней - не больше двух
    if (node.attrs.kind === 'digit') add(env, id, pos + 1, Math.min(pos + 4, pos + node.nodeSize), 'E', 'LIST-MARKER',
      'Цифры со скобкой «1)» - только для вложенного перечисления; на первом уровне - «–» или «а)»', '4.4.4',
      [{ label: 'Сделать «а)»', apply: (ed) => { const n = ed.state.doc.nodeAt(pos); if (n) ed.view.dispatch(ed.state.tr.setNodeMarkup(pos, undefined, { ...n.attrs, kind: 'letter' })); } },
        { label: 'Сделать «–»', apply: (ed) => { const n = ed.state.doc.nodeAt(pos); if (n) ed.view.dispatch(ed.state.tr.setNodeMarkup(pos, undefined, { ...n.attrs, kind: 'dash' })); } }], 'digit');
    node.descendants((sub, sp) => {
      if (sub.type.name !== 'stoList') return true;
      let depth = 0;
      const $s = env.doc!.resolve(pos + 1 + sp);
      for (let d = $s.depth; d > 0; d--) if ($s.node(d).type.name === 'stoList') depth++;
      if (depth >= 2) {
        const at = pos + 1 + sp;
        add(env, (sub.attrs?.id as string) || id, at + 1, Math.min(at + 4, at + sub.nodeSize), 'E', 'LIST-DEPTH',
          'Третий уровень перечисления СТО не предусматривает (4.4.4: перечисление и его детализация) - поднимите уровень (Shift+Tab)', '4.4.4', [], sub.attrs?.id || String(at));
        return false;
      }
      return true;
    });
    // позиции: со строчной, «;» в конце, последняя - «.»
    const items: { para: PMNode; pos: number; hasSub: boolean }[] = [];
    const itemId = (it: { para: PMNode }) => (it.para.attrs?.id as string) || id;
    const walk = (list: PMNode, lpos: number) => {
      list.forEach((li, off) => {
        const lip = lpos + 1 + off;
        let first: { n: PMNode; p: number } | null = null;
        let sub: { n: PMNode; p: number } | null = null;
        li.forEach((c, o2) => { if (c.type.name === 'paragraph' && !first) first = { n: c, p: lip + 1 + o2 }; if (c.type.name === 'stoList') sub = { n: c, p: lip + 1 + o2 }; });
        if (first) items.push({ para: (first as any).n, pos: (first as any).p, hasSub: !!sub });
        if (sub) walk((sub as any).n, (sub as any).p);
      });
    };
    walk(node, pos);
    const texts = items.filter((it) => it.para.content.size);
    texts.forEach((it, i) => {
      const bt = blockText(it.para, it.pos);
      const s = bt.text.replace(/[\s ]+$/, '');
      const lead = s.search(/\S/);
      // 4.4.3, 4.4.4: текст позиции - со строчной буквы («следует» - ошибка, как у sto_check), кроме имён
      // собственных и аббревиатур (по написанию в тексте и словарю)
      const fw = lead >= 0 ? s.slice(lead).match(/^[«"(]?([А-ЯЁ][А-Яа-яЁё-]*)/) : null;
      if (fw) {
        if (!env.proper) env.proper = properWordsOf(env.doc);
        const w0 = fw[1];
        const at = lead + (fw[0].length - w0.length);
        if (!isProperWord(w0, s.slice(at + w0.length), env.proper)) {
          add(env, itemId(it), bt.map[at], bt.map[at + 1], 'E', 'LIST-CASE', 'Позицию перечисления начинают со строчной буквы (кроме имён собственных и аббревиатур)', '4.4.3',
            [replaceFix('Сделать строчной', bt.map[at], bt.map[at + 1], s[at].toLowerCase()), { label: 'Со строчной во всём перечислении', apply: (ed) => lowerListItems(ed, pos) }], s.slice(0, 25));
        }
      }
      if (env.listPunctAuto || it.hasSub) return;
      const want = i === texts.length - 1 ? '.' : ';';
      if (!s.endsWith(want) && !/[!?…]$/.test(s)) {
        const tail = s.match(/[.;,:]+$/);
        const a = bt.map[s.length - (tail ? tail[0].length : 0)]; const b = bt.map[s.length];
        // ошибка, как у sto_check (LIST-PUNCT - E): иначе живая проверка «зелёная», а сборка - нет
        add(env, itemId(it), Math.max(bt.map[0], b - 1), b, 'E', 'LIST-PUNCT',
          want === ';' ? 'В конце позиции перечисления ставят «;»' : 'В конце последней позиции перечисления ставят «.»', '4.4.5',
          [punctFix(want, it.pos), { label: 'Расставить знаки во всём перечислении', apply: (ed) => fixListPunct(ed, pos) }], s.slice(-25));
      }
    });
    return;
  }
  if (t === 'note' && index > 0 && parent.child(index - 1).type.name === 'stoTable') {
    // writer выводит такое примечание строкой внутри таблицы (4.10.7) - в редакторе показываем это заранее
    const tblPos = pos - parent.child(index - 1).nodeSize;
    add(env, id, pos + 1, Math.min(pos + 3, pos + node.nodeSize), 'I', 'NOTE-TABLE-POS',
      'Примечание сразу после таблицы при экспорте станет её последней строкой («Примечание – …» внутри таблицы, 4.10.7)', '4.10.7',
      [{ label: 'Сделать примечанием к таблице', apply: (ed) => {
        const tbl = ed.state.doc.nodeAt(tblPos);
        const note = ed.state.doc.nodeAt(pos);
        if (!tbl || tbl.type.name !== 'stoTable' || !note || note.type.name !== 'note') return;
        const paras: PMNode[] = [];
        note.forEach((c) => { if (c.type.name === 'paragraph') paras.push(c); });
        const tr = ed.state.tr.delete(pos, pos + note.nodeSize);
        const last = tbl.lastChild;
        if (last && last.type.name === 'tableNote') tr.insert(tblPos + tbl.nodeSize - 2, paras);
        else tr.insert(tblPos + tbl.nodeSize - 1, ed.schema.nodes.tableNote.create(null, paras));
        ed.view.dispatch(tr);
      } }], 'после таблицы');
  }
  if (t === 'note') {
    node.forEach((p, off) => {
      if (p.type.name !== 'paragraph' || !p.content.size) return;
      const bt = blockText(p, pos + 1 + off);
      const s = bt.text.replace(/[\s ]+$/, '');
      const lbl = s.match(/^(Примечани[ея])[  ]*[–—-]?[  ]*/);
      if (lbl) add(env, id, bt.map[0], bt.map[lbl[0].length], 'I', 'NOTE-LABEL', 'Слово «Примечание –» ставится автоматически', '4.10.5',
        [replaceFix('Убрать', bt.map[0], bt.map[lbl[0].length], '')], lbl[0]);
      if (!/[.!?…]$/.test(s)) add(env, id, Math.max(bt.map[0], bt.map[s.length] - 1), bt.map[s.length], 'W', 'NOTE-DOT',
        'Примечание заканчивается точкой (при экспорте поставится сама)', '4.10', [replaceFix('Поставить точку', bt.map[s.length], bt.map[s.length], '.')], s.slice(-25));
    });
  }
}

/** «Поставить «;»» у позиции: по свежему тексту абзаца (идемпотентно - знак уже стоит, ничего не делаем). */
function punctFix(want: string, paraPos: number): Fix {
  return { label: `Поставить «${want}»`, apply: (ed) => {
    const para = ed.state.doc.nodeAt(paraPos);
    if (!para || para.type.name !== 'paragraph') return;
    const bt = blockText(para, paraPos);
    const s = bt.text.replace(/[\s\u00a0]+$/, '');
    if (s.endsWith(want) || /[!?…]$/.test(s)) return;
    const tail = s.match(/[.;,:]+$/);
    ed.view.dispatch(ed.state.tr.insertText(want, bt.map[s.length - (tail ? tail[0].length : 0)], bt.map[s.length]));
  } };
}

/** Все знаки перечисления: «;» у позиций, «.» у последней. */
export function fixListPunct(ed: Editor, listPos: number) {
  const list = ed.state.doc.nodeAt(listPos);
  if (!list) return;
  const paras: { n: PMNode; p: number; sub: boolean }[] = [];
  const walk = (l: PMNode, lp: number) => l.forEach((li, off) => {
    const lip = lp + 1 + off;
    let sub: [PMNode, number] | null = null;
    let first: [PMNode, number] | null = null;
    li.forEach((c, o2) => { if (c.type.name === 'paragraph' && !first) first = [c, lip + 1 + o2]; if (c.type.name === 'stoList') sub = [c, lip + 1 + o2]; });
    if (first && (first as any)[0].content.size) paras.push({ n: (first as any)[0], p: (first as any)[1], sub: !!sub });
    if (sub) walk((sub as any)[0], (sub as any)[1]);
  });
  walk(list, listPos);
  const tr = ed.state.tr;
  paras.forEach((it, i) => {
    if (it.sub) return;
    const bt = blockText(it.n, it.p);
    const s = bt.text.replace(/[\s ]+$/, '');
    const want = i === paras.length - 1 ? '.' : ';';
    if (s.endsWith(want) || /[!?…]$/.test(s)) return;
    const tail = s.match(/[.;,:]+$/);
    tr.insertText(want, tr.mapping.map(bt.map[s.length - (tail ? tail[0].length : 0)]), tr.mapping.map(bt.map[s.length]));
  });
  if (tr.docChanged) ed.view.dispatch(tr);
}

/** Все позиции перечисления (и вложенных) - со строчной буквы, кроме имён собственных и аббревиатур (4.4.3). */
export function lowerListItems(ed: Editor, listPos: number) {
  const list = ed.state.doc.nodeAt(listPos);
  if (!list) return;
  const proper = properWordsOf(ed.state.doc);
  const tr = ed.state.tr;
  list.descendants((n, p) => {
    if (n.type.name !== 'paragraph') return true;
    const bt = blockText(n, listPos + 1 + p);
    const s = bt.text;
    const lead = s.search(/\S/);
    const fw = lead >= 0 ? s.slice(lead).match(/^[«"(]?([А-ЯЁ][А-Яа-яЁё-]*)/) : null;
    if (fw) {
      const at = lead + (fw[0].length - fw[1].length);
      if (!isProperWord(fw[1], s.slice(at + fw[1].length), proper)) {
        tr.insertText(s[at].toLowerCase(), tr.mapping.map(bt.map[at]), tr.mapping.map(bt.map[at + 1]));
      }
    }
    return false;
  });
  if (tr.docChanged) ed.view.dispatch(tr);
}

function atomRules(env: Env, node: PMNode, pos: number, blockId: string | null) {
  const t = node.type.name;
  if (t === 'xref') {
    const target = node.attrs.target;
    if (env.nb.byId.has(target)) return;
    let exists: PMNode | null = null;
    appStore.getState().editor?.state.doc.descendants((n) => { if (n.attrs?.id === target) exists = n; return !exists; });
    const ex = exists as PMNode | null;
    if (ex && ex.type.name === 'formula') {
      add(env, blockId, pos, pos + 1, 'E', 'XREF-UNNUMBERED', 'Ссылка на формулу без номера - пронумеруйте формулу', '4.6',
        [{ label: 'Нумеровать формулу', apply: (ed) => { ed.state.doc.descendants((n, p) => { if (n.attrs?.id === target) { ed.view.dispatch(ed.state.tr.setNodeMarkup(p, undefined, { ...n.attrs, numbered: true })); return false; } return true; }); } },
          replaceFix('Удалить ссылку', pos, pos + 1, '')], target);
    } else {
      add(env, blockId, pos, pos + 1, 'E', 'XREF-BROKEN', ex ? 'Ссылка на объект без номера (пустой заголовок, рисунок без файла)' : 'Ссылка на удалённый объект',
        '4.14', [replaceFix('Удалить ссылку', pos, pos + 1, ''), { label: 'Выбрать другой объект…', apply: (ed) => {
          ed.view.dispatch(ed.state.tr.delete(pos, pos + 1).setSelection(TextSelection.create(ed.state.doc, pos)));
          void import('../../core/ctx').then((m) => m.ctx.run('refs.xref'));
        } }], target);
    }
  } else if (t === 'cite') {
    const items = node.attrs.items || [];
    if (!items.length || items.some((it: any) => !env.nb.sources.has(it.source))) {
      add(env, blockId, pos, pos + 1, 'E', 'CITE-BROKEN', 'Ссылка на источник, которого нет в библиотеке', '4.14',
        [replaceFix('Удалить ссылку', pos, pos + 1, '')], JSON.stringify(items).slice(0, 40));
    }
  } else if (t === 'footnote') {
    if (env.doc) {
      const $p = env.doc.resolve(pos);
      for (let d = $p.depth; d > 0; d--) {
        if (['tableCell', 'tableHeader'].includes($p.node(d).type.name)) {
          add(env, blockId, pos, pos + 1, 'I', 'FOOTNOTE-TABLE', 'Сноска в таблице при экспорте станет знаком «*» и строкой «* текст» в конце таблицы (4.10.9)', '4.10.9', [], node.attrs.id);
          break;
        }
      }
    }
    const content = node.attrs.content || [];
    const txt = content.map((n: any) => (n.type === 'text' ? n.text : ' ')).join('').trim();
    if (!txt) add(env, blockId, pos, pos + 1, 'E', 'FOOTNOTE-EMPTY', 'Пустая сноска', '4.10.9', [replaceFix('Удалить сноску', pos, pos + 1, '')], node.attrs.id);
    else if (/\.$/.test(txt) && !/\.\.$|(?:^|\s)\S\.$/.test(txt)) {
      add(env, blockId, pos, pos + 1, 'E', 'FOOTNOTE-DOT', 'Точка в конце текста сноски не ставится', '4.10.11',
        [{ label: 'Убрать точку', apply: (ed) => { void import('./refs').then((m) => {
          const n = ed.state.doc.nodeAt(pos);
          if (n?.type.name === 'footnote') ed.view.dispatch(ed.state.tr.setNodeMarkup(pos, undefined, { ...n.attrs, content: m.stripDot(n.attrs.content) }));
        }); } }], node.attrs.id);
    }
  }
}

// «Пример – …» / «Примеры» в начале абзаца (как clean.ts и sto_check): слово - полужирным курсивом (4.15.2), не снимается
const EXAMPLE_LABEL = /^(\s*)(Примеры?)(?:\s*(?:[–—:.]|$)|\s+-\s|\s+\d+(?!\d))/;

/** Полужирный в тексте (не заголовок, не головка таблицы): writer при экспорте его снимает - по профилю полужирные
 *  только заголовки (стилем), головка таблицы (4.9.8) и титул. Подсказка показывает, что в Word его не будет. */
function boldRules(env: Env, node: PMNode, pos: number, blockId: string | null) {
  if (node.type.name !== 'paragraph' || !env.doc) return;
  const $p = env.doc.resolve(pos + 1);
  for (let d = $p.depth; d > 0; d--) if ($p.node(d).type.name === 'tableHeader') return;
  const ex = EXAMPLE_LABEL.exec(node.textContent);
  const keepTo = ex ? ex[1].length + ex[2].length : 0;
  const found: { from: number; to: number; text: string }[] = [];
  let idx = 0;
  node.forEach((c, off) => {
    const len = c.isText ? (c.text || '').length : c.textContent.length;
    if (c.isText && c.marks.some((m) => m.type.name === 'bold') && idx + len > keepTo) {
      const skip = Math.max(0, keepTo - idx);
      const from = pos + 1 + off + skip;
      const to = pos + 1 + off + len;
      const s = (c.text || '').slice(skip);
      const last = found[found.length - 1];
      if (last && last.to === from) { last.to = to; last.text += s; } else found.push({ from, to, text: s });
    }
    idx += len;
  });
  for (const f of found) {
    if (!f.text.trim()) continue;
    add(env, blockId, f.from, f.to, 'I', 'BODY-BOLD',
      'Полужирный в тексте при экспорте снимается: по профилю полужирные только заголовки и головка таблицы', '4.1.5',
      [{ label: 'Снять полужирный', apply: (ed) => { ed.view.dispatch(ed.state.tr.removeMark(f.from, f.to, ed.schema.marks.bold)); } },
        { label: 'Снять полужирный во всём тексте', apply: (ed) => {
          const tr = ed.state.tr;
          for (const x of scanDoc(ed.state.doc, numberingStore.get())) if (x.code === 'BODY-BOLD') tr.removeMark(x.from, x.to, ed.schema.marks.bold);
          if (tr.docChanged) ed.view.dispatch(tr);
        } }], f.text);
  }
}

/** Все замечания документа. */
let memo: { doc: PMNode; nb: Numbering; meta: unknown; stamp: number; mention: number | undefined; out: InlineIssue[] } | null = null;

export function scanDoc(doc: PMNode, nb: Numbering): InlineIssue[] {
  const meta = appStore.getState().meta;
  const mention = overlay.getState().mention?.from;
  // кэш: тот же документ, номера, метаданные и список пропущенных - тот же результат (панель вызывает часто)
  if (memo && memo.doc === doc && memo.nb === nb && memo.meta === meta && memo.stamp === ignores.stamp && memo.mention === mention) return memo.out;
  const out = scanDocRaw(doc, nb, meta);
  memo = { doc, nb, meta, stamp: ignores.stamp, mention, out };
  return out;
}

function scanDocRaw(doc: PMNode, nb: Numbering, meta: any): InlineIssue[] {
  const env: Env = { nb, out: [], listPunctAuto: meta?.options?.list_punct === 'fix', doc };
  const visit = (node: PMNode, pos: number, parent: PMNode, index: number, blockId: string | null) => {
    const t = node.type.name;
    if (t === 'listing' || t === 'formula') return;
    nodeRules(env, node, pos, parent, index);
    const id = (node.attrs?.id as string) || blockId;
    if (node.isTextblock) {
      const bt = blockText(node, pos);
      if (bt.text.replace(/[\s ]/g, '')) textRules(env, bt, id, t);
      boldRules(env, node, pos, id);
      bt.atoms.forEach((a, i) => atomRules(env, a, bt.map[i], id));
      return;
    }
    node.forEach((c, off, i) => visit(c, pos + 1 + off, node, i, id));
  };
  doc.forEach((c, off, i) => visit(c, off, doc, i, null));
  const ign = ignores.list(appStore.getState().projectId);
  return env.out.filter((x) => !ign.has(x.key)).sort((a, b) => a.from - b.from);
}

// ------------------------------------------------------------------------------------------------ плагин
interface LState { issues: InlineIssue[]; deco: DecorationSet; version: number }
export const lintKey = new PluginKey<LState>('stoInlineLint');
let latest: InlineIssue[] = [];
export const inlineIssues = () => latest;

/** Применить исправление i замечания по свежей проверке текущего документа (позиции могли сдвинуться):
 *  то же замечание (ключ), ближайшее к прежнему месту. false - замечания уже нет. */
export function applyFix(ed: Editor, issue: { key: string; from: number }, i = 0): boolean {
  const fresh = scanDoc(ed.state.doc, numberingStore.get()).filter((x) => x.key === issue.key && x.fixes[i]);
  if (!fresh.length) return false;
  fresh.sort((a, b) => Math.abs(a.from - issue.from) - Math.abs(b.from - issue.from));
  fresh[0].fixes[i].apply(ed);
  return true;
}

function decorate(doc: PMNode, issues: InlineIssue[]): DecorationSet {
  if (!prefs.lintInline) return DecorationSet.empty;
  const d: Decoration[] = [];
  for (const x of issues) {
    let from = x.from; let to = Math.max(x.to, x.from + 1);
    if (to > doc.content.size) { to = doc.content.size; from = Math.max(0, to - 1); }
    d.push(Decoration.inline(from, to, { class: `sto-lint sto-lint-${x.sev}`, 'data-lint': x.key }, { issue: x }));
  }
  return DecorationSet.create(doc, d);
}

export function rescan(view: EditorView) {
  const issues = scanDoc(view.state.doc, numberingStore.get());
  latest = issues;
  view.dispatch(view.state.tr.setMeta(lintKey, issues).setMeta('addToHistory', false));
}

export const InlineLintExtension = Extension.create({
  name: 'stoInlineLint',
  addProseMirrorPlugins() {
    let timer: number | undefined;
    return [new Plugin<LState>({
      key: lintKey,
      state: {
        init: () => ({ issues: [], deco: DecorationSet.empty, version: 0 }),
        apply: (tr, prev, _o, state: EditorState) => {
          const issues = tr.getMeta(lintKey) as InlineIssue[] | undefined;
          if (issues) return { issues, deco: decorate(state.doc, issues), version: prev.version + 1 };
          if (tr.docChanged) {
            const mapped = prev.issues.map((x) => ({ ...x, from: tr.mapping.map(x.from), to: tr.mapping.map(x.to, -1) })).filter((x) => x.to >= x.from);
            return { issues: mapped, deco: prev.deco.map(tr.mapping, tr.doc), version: prev.version };
          }
          return prev;
        },
      },
      props: {
        decorations: (state) => lintKey.getState(state)?.deco,
        handleDOMEvents: {
          mouseover: (view, e) => {
            const el = (e.target as HTMLElement)?.closest?.('.sto-lint') as HTMLElement | null;
            if (!el) return false;
            const st = lintKey.getState(view.state);
            const keys = new Set<string>();
            let cur: HTMLElement | null = el;
            while (cur && cur.classList?.contains('sto-lint')) { keys.add(cur.getAttribute('data-lint') || ''); cur = cur.parentElement?.closest('.sto-lint') || null; }
            const pos = view.posAtDOM(el, 0);
            const hits = (st?.issues || []).filter((x) => keys.has(x.key) && x.from <= pos + 1 && pos <= Math.max(x.to, x.from + 1));
            if (!hits.length) return false;
            const r = el.getBoundingClientRect();
            const tip = overlay.getState().tip;
            if (tip && tip.issues[0]?.key === hits[0].key) return false;
            overlay.getState().set({ tip: { x: r.left, y: r.bottom + 4, issues: hits } });
            return false;
          },
        },
      },
      view: (view) => {
        timer = window.setTimeout(() => rescan(view), 200);
        return {
          update: (v, prevState) => {
            if (v.state.doc !== prevState.doc) {
              window.clearTimeout(timer);
              timer = window.setTimeout(() => { if (!v.isDestroyed) rescan(v); }, 300);
              if (overlay.getState().tip) overlay.getState().set({ tip: null });
            }
          },
          destroy: () => { window.clearTimeout(timer); latest = []; },
        };
      },
    })];
  },
});

